import React, { Suspense, useEffect } from 'react';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import { LoadingWithCaption } from './components/LoadingWithCaption';
import zhCN from 'antd/locale/zh_CN';
import 'dayjs/locale/zh-cn';
import ProtectedRoute from './components/ProtectedRoute';
import YanxuanSellerWorkbenchGate from './components/YanxuanSellerWorkbenchGate';
import AuthCrossTabSync from './components/AuthCrossTabSync';
import CompanyCenterGuard from './components/CompanyCenterGuard';
import { WorkbenchDraggableModals } from './hooks/useDraggableAntModals';
import { useAuthStore } from './store/authStore';
import { UserRole, type User } from './types';
import { detectMobileOrMiniProgram } from './utils/supplierView';
import {
  APPROVAL_WORKBENCH_ROLES,
  BUYER_WORKBENCH_ROLES,
  PLATFORM_WORKBENCH_ROLES,
  SETTINGS_COMPANY_ROLES,
  SETTINGS_USERS_ROLES,
  SETTINGS_WORKFLOW_ROLES,
  YANXUAN_WORKBENCH_ROLES,
  resolveDefaultRouteByRole,
} from './utils/workbenchView';
import {
  dualRoutePath,
  oppositeDualRoutePath,
  type DualRouteKey,
} from './utils/workbenchRoutes';
import './App.css';
import './components/workbench/workbench-shell.css';
import SiteBeianFooter from './components/SiteBeianFooter';
import LegalConsentGate from './components/legal/LegalConsentGate';
import { LegalDocumentViewerProvider } from './components/legal/LegalDocumentViewer';
import { prefetchPublicEntryPages, prefetchWorkbenchByRole } from './utils/prefetchWorkbench';
import { resolvePostLoginPath } from './utils/loginReturnTo';
import { isYanxuanEnabled } from './config/yanxuanFeature';

const Login = lazyWithRetry(() => import('./pages/Login'), 'Login');
const Portal = lazyWithRetry(() => import('./pages/Portal'), 'Portal');
const BuyerWorkbench = lazyWithRetry(() => import('./pages/Buyer'), 'BuyerWorkbench');
const BuyerH5 = lazyWithRetry(() => import('./pages/BuyerH5'), 'BuyerH5');
const ApprovalWorkbench = lazyWithRetry(() => import('./pages/Approval'), 'ApprovalWorkbench');
const ApprovalH5 = lazyWithRetry(() => import('./pages/ApprovalH5'), 'ApprovalH5');
const PlatformWorkbench = lazyWithRetry(() => import('./pages/Platform'), 'PlatformWorkbench');
const PlatformH5 = lazyWithRetry(() => import('./pages/PlatformH5'), 'PlatformH5');
const YanxuanPlatformWorkbench = lazyWithRetry(
  () => import('./pages/YanxuanPlatform'),
  'YanxuanPlatformWorkbench',
);
const YanxuanSupplierOrders = lazyWithRetry(
  () => import('./pages/YanxuanSupplierOrders'),
  'YanxuanSupplierOrders',
);
const SupplierWorkbench = lazyWithRetry(() => import('./pages/SupplierWorkbenchV2'), 'SupplierWorkbench');
const SupplierH5 = lazyWithRetry(() => import('./pages/SupplierH5'), 'SupplierH5');
const SupplierH5Login = lazyWithRetry(() => import('./pages/SupplierH5/Login'), 'SupplierH5Login');
const Unauthorized = lazyWithRetry(() => import('./pages/Unauthorized'), 'Unauthorized');
const InvitedRegister = lazyWithRetry(() => import('./pages/Register/InvitedRegister'), 'InvitedRegister');
const UserAccountManage = lazyWithRetry(() => import('./pages/Settings/UserAccountManage'), 'UserAccountManage');
const WorkflowConfig = lazyWithRetry(() => import('./pages/Settings/WorkflowConfig'), 'WorkflowConfig');
const CompanySettings = lazyWithRetry(() => import('./pages/Settings/CompanySettings'), 'CompanySettings');
const UserAccountManageH5 = lazyWithRetry(() => import('./pages/Settings/UserAccountManage'), 'UserAccountManageH5');
const WorkflowConfigH5 = lazyWithRetry(() => import('./pages/Settings/WorkflowConfig'), 'WorkflowConfigH5');
const CompanySettingsH5 = lazyWithRetry(() => import('./pages/Settings/CompanySettings'), 'CompanySettingsH5');
const ImPopupPage = lazyWithRetry(() => import('./pages/ImPopup'), 'ImPopupPage');
const LegalDocumentPage = lazyWithRetry(() => import('./pages/Legal/LegalDocumentPage'), 'LegalDocumentPage');
const SetPasswordRequired = lazyWithRetry(
  () => import('./pages/Account/SetPasswordRequired'),
  'SetPasswordRequired',
);

const RouteLoading: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5',
    }}
  >
    <LoadingWithCaption size="large" caption="页面加载中...">
      <div style={{ minHeight: 120, minWidth: 160 }} aria-hidden />
    </LoadingWithCaption>
  </div>
);

function renderDualModeElement(
  routeKey: DualRouteKey,
  preferMobileLayout: boolean,
  desktopElement: React.ReactNode,
  h5Element: React.ReactNode,
  isH5Route: boolean,
) {
  if (isH5Route) {
    return preferMobileLayout ? (
      h5Element
    ) : (
      <Navigate to={oppositeDualRoutePath(routeKey, true)} replace />
    );
  }
  return preferMobileLayout ? (
    <Navigate to={dualRoutePath(routeKey, true)} replace />
  ) : (
    desktopElement
  );
}

function YanxuanBuyerProductDetailRoute({ h5Route }: { h5Route: boolean }) {
  const { id } = useParams();
  const preferMobileLayout = detectMobileOrMiniProgram();
  const productId = String(id || '').trim();
  if (!productId) {
    return <Navigate to={preferMobileLayout ? '/buyer-h5' : '/buyer'} replace />;
  }
  if (h5Route && !preferMobileLayout) {
    return <Navigate to={`/buyer/yanxuan/product/${productId}`} replace />;
  }
  if (!h5Route && preferMobileLayout) {
    return <Navigate to={`/buyer-h5/yanxuan/product/${productId}`} replace />;
  }
  return preferMobileLayout ? <BuyerH5 /> : <BuyerWorkbench />;
}

function BuyerProductDetailRoute({ h5Route }: { h5Route: boolean }) {
  const { id } = useParams();
  const preferMobileLayout = detectMobileOrMiniProgram();
  const productId = String(id || '').trim();
  if (!productId) {
    return <Navigate to={preferMobileLayout ? '/buyer-h5' : '/buyer'} replace />;
  }
  if (h5Route && !preferMobileLayout) {
    return <Navigate to={`/buyer/product/${productId}`} replace />;
  }
  if (!h5Route && preferMobileLayout) {
    return <Navigate to={`/buyer-h5/product/${productId}`} replace />;
  }
  return preferMobileLayout ? <BuyerH5 /> : <BuyerWorkbench />;
}

function App() {
  const { initAuth, isAuthenticated, user, authInitialized } = useAuthStore();
  const preferMobileLayout = detectMobileOrMiniProgram();
  /** 严选总开关：关闭时 `/yanxuan*` 等严选路由全部重定向，行为与 1.0 现网一致 */
  const yanxuanEnabled = isYanxuanEnabled();

  useEffect(() => {
    initAuth();
    prefetchPublicEntryPages();
  }, [initAuth]);

  useEffect(() => {
    if (authInitialized && isAuthenticated && user?.role) {
      prefetchWorkbenchByRole(user.role);
    }
  }, [authInitialized, isAuthenticated, user?.role]);

  // 根据用户角色获取默认路由
  const getDefaultRoute = () => {
    if (user) {
      return resolvePostLoginPath(user, { preferMobileLayout });
    }
    return resolveDefaultRouteByRole(undefined, preferMobileLayout);
  };

  /** 严选关闭时，严选路由的去向：未登录回登录页；严选管理员无可用工作台 → 无权限页；其余回角色默认页 */
  const yanxuanDisabledRedirect = () => {
    if (!isAuthenticated || !user) return '/login';
    if (user.role === UserRole.YANXUAN_ADMIN) return '/unauthorized';
    return getDefaultRoute();
  };

  return (
    <ConfigProvider locale={zhCN}>
      <AntApp>
      <Router>
        <AuthCrossTabSync />
        <WorkbenchDraggableModals />
        <LegalDocumentViewerProvider>
        <div className="app-page-shell">
          <Suspense fallback={<RouteLoading />}>
            <Routes>
          {/* 网站首页：/ 为根路径，/home 为别名 */}
          <Route path="/home" element={<Portal />} />
          
          {/* 公开路由（无需登录） */}
          {/* 已登录供应商：严选开启时由 Login 内 capability 探测决定进直采还是严选工作台；关闭时与 1.0 一致直接路由层跳转 */}
          <Route 
            path="/login" 
            element={
              !authInitialized ? (
                <RouteLoading />
              ) : isAuthenticated && user && (!yanxuanEnabled || user.role !== UserRole.SUPPLIER) ? (
                <Navigate to={resolvePostLoginPath(user)} replace />
              ) : (
                <Login />
              )
            } 
          />
          <Route
            path="/yanxuan/login"
            element={
              !yanxuanEnabled ? (
                <Navigate to="/login" replace />
              ) : !authInitialized ? (
                <RouteLoading />
              ) : isAuthenticated && user?.role === UserRole.YANXUAN_ADMIN ? (
                <Navigate to="/yanxuan" replace />
              ) : (
                <Login variant="yanxuan" />
              )
            }
          />
          <Route
            path="/account/set-password"
            element={
              <ProtectedRoute>
                <SetPasswordRequired />
              </ProtectedRoute>
            }
          />
          
          {/* 旧链接兼容 */}
          <Route path="/settings/invitations" element={<Navigate to="/settings/users" replace />} />
          <Route path="/register" element={<Navigate to="/" replace />} />
          <Route path="/register/property" element={<Navigate to="/login" replace />} />
          <Route path="/register/supplier" element={<Navigate to="/login" replace />} />
          <Route path="/register/invited" element={<Navigate to={`/invited${typeof window !== 'undefined' ? window.location.search : ''}`} replace />} />
          
          <Route path="/register-property" element={<Navigate to="/login" replace />} />
          <Route path="/register-supplier" element={<Navigate to="/login" replace />} />
          <Route path="/invited" element={<InvitedRegister />} />
          <Route
            path="/resubmit-registration"
            element={<Navigate to="/login" replace />}
          />
          <Route path="/legal/:slug" element={<LegalDocumentPage />} />
          <Route
            path="/supplier-h5-login"
            element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <SupplierH5Login />}
          />
          
          {/* 认证路由 */}
          <Route
            path="/im-popup"
            element={
              <ProtectedRoute>
                <ImPopupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer"
            element={
              <ProtectedRoute allowedRoles={BUYER_WORKBENCH_ROLES}>
                {renderDualModeElement(
                  'buyer',
                  preferMobileLayout,
                  <BuyerWorkbench />,
                  <BuyerH5 />,
                  false,
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer-h5"
            element={
              <ProtectedRoute allowedRoles={BUYER_WORKBENCH_ROLES}>
                {renderDualModeElement(
                  'buyer',
                  preferMobileLayout,
                  <BuyerWorkbench />,
                  <BuyerH5 />,
                  true,
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/product/:id"
            element={
              <ProtectedRoute allowedRoles={BUYER_WORKBENCH_ROLES}>
                <BuyerProductDetailRoute h5Route={false} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer-h5/product/:id"
            element={
              <ProtectedRoute allowedRoles={BUYER_WORKBENCH_ROLES}>
                <BuyerProductDetailRoute h5Route={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/yanxuan/product/:id"
            element={
              !yanxuanEnabled ? (
                <Navigate to="/buyer" replace />
              ) : (
                <ProtectedRoute allowedRoles={BUYER_WORKBENCH_ROLES}>
                  <YanxuanBuyerProductDetailRoute h5Route={false} />
                </ProtectedRoute>
              )
            }
          />
          <Route
            path="/buyer-h5/yanxuan/product/:id"
            element={
              !yanxuanEnabled ? (
                <Navigate to="/buyer-h5" replace />
              ) : (
                <ProtectedRoute allowedRoles={BUYER_WORKBENCH_ROLES}>
                  <YanxuanBuyerProductDetailRoute h5Route={true} />
                </ProtectedRoute>
              )
            }
          />
          <Route
            path="/central-procurement"
            element={
              <ProtectedRoute allowedRoles={[UserRole.CENTRAL_PURCHASER]}>
                {renderDualModeElement(
                  'centralProcurement',
                  preferMobileLayout,
                  <BuyerWorkbench />,
                  <BuyerH5 />,
                  false,
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/central-procurement-h5"
            element={
              <ProtectedRoute allowedRoles={[UserRole.CENTRAL_PURCHASER]}>
                {renderDualModeElement(
                  'centralProcurement',
                  preferMobileLayout,
                  <BuyerWorkbench />,
                  <BuyerH5 />,
                  true,
                )}
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/approval"
            element={
              <ProtectedRoute allowedRoles={APPROVAL_WORKBENCH_ROLES}>
                {renderDualModeElement(
                  'approval',
                  preferMobileLayout,
                  <ApprovalWorkbench />,
                  <ApprovalH5 />,
                  false,
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/approval-h5"
            element={
              <ProtectedRoute allowedRoles={APPROVAL_WORKBENCH_ROLES}>
                {renderDualModeElement(
                  'approval',
                  preferMobileLayout,
                  <ApprovalWorkbench />,
                  <ApprovalH5 />,
                  true,
                )}
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/platform"
            element={
              <ProtectedRoute allowedRoles={PLATFORM_WORKBENCH_ROLES}>
                {renderDualModeElement(
                  'platform',
                  preferMobileLayout,
                  <PlatformWorkbench />,
                  <PlatformH5 />,
                  false,
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform-h5"
            element={
              <ProtectedRoute allowedRoles={PLATFORM_WORKBENCH_ROLES}>
                {renderDualModeElement(
                  'platform',
                  preferMobileLayout,
                  <PlatformWorkbench />,
                  <PlatformH5 />,
                  true,
                )}
              </ProtectedRoute>
            }
          />

          {/* 严选配货工作台 / 严选供应商工作台（同一路由按角色分支）；严选关闭时整段重定向 */}
          <Route
            path="/yanxuan"
            element={
              !yanxuanEnabled ? (
                !authInitialized ? (
                  <RouteLoading />
                ) : (
                  <Navigate to={yanxuanDisabledRedirect()} replace />
                )
              ) : (
                <ProtectedRoute allowedRoles={YANXUAN_WORKBENCH_ROLES} loginPath="/yanxuan/login">
                  <YanxuanSellerWorkbenchGate>
                    <YanxuanPlatformWorkbench />
                  </YanxuanSellerWorkbenchGate>
                </ProtectedRoute>
              )
            }
          />

          {/* 供应商 · 严选订单（独立路由，不混入 1.0 订单分桶） */}
          <Route
            path="/supplier-desktop/yanxuan-orders"
            element={
              !yanxuanEnabled ? (
                <Navigate to="/supplier-desktop" replace />
              ) : (
                <ProtectedRoute allowedRoles={[UserRole.SUPPLIER]}>
                  <YanxuanSupplierOrders />
                </ProtectedRoute>
              )
            }
          />
          <Route
            path="/supplier-h5/yanxuan-orders"
            element={
              !yanxuanEnabled ? (
                <Navigate to="/supplier-h5" replace />
              ) : (
                <ProtectedRoute allowedRoles={[UserRole.SUPPLIER]}>
                  <YanxuanSupplierOrders compact />
                </ProtectedRoute>
              )
            }
          />

          {/* 供应商工作台 */}
          <Route
            path="/supplier"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPPLIER]}>
                <Navigate to={dualRoutePath('supplier', preferMobileLayout)} replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/supplier-desktop"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPPLIER]}>
                {renderDualModeElement(
                  'supplier',
                  preferMobileLayout,
                  <SupplierWorkbench />,
                  <SupplierH5 />,
                  false,
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/supplier-h5"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPPLIER]}>
                {renderDualModeElement(
                  'supplier',
                  preferMobileLayout,
                  <SupplierWorkbench />,
                  <SupplierH5 />,
                  true,
                )}
              </ProtectedRoute>
            }
          />
          
          {/* 企业管理中心（企业管理员专用） */}
          {/* 企业管理中心（物业/供应商企业管理员专用，平台 admin 不进入） */}
          <Route
            path="/settings/company"
            element={
              <ProtectedRoute allowedRoles={SETTINGS_COMPANY_ROLES}>
                <CompanyCenterGuard>
                  {renderDualModeElement(
                    'settingsCompany',
                    preferMobileLayout,
                    <CompanySettings />,
                    <CompanySettingsH5 />,
                    false,
                  )}
                </CompanyCenterGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/company-h5"
            element={
              <ProtectedRoute allowedRoles={SETTINGS_COMPANY_ROLES}>
                <CompanyCenterGuard>
                  {renderDualModeElement(
                    'settingsCompany',
                    preferMobileLayout,
                    <CompanySettings />,
                    <CompanySettingsH5 />,
                    true,
                  )}
                </CompanyCenterGuard>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings/users"
            element={
              <ProtectedRoute allowedRoles={SETTINGS_USERS_ROLES}>
                <CompanyCenterGuard>
                  {renderDualModeElement(
                    'settingsUsers',
                    preferMobileLayout,
                    <UserAccountManage />,
                    <UserAccountManageH5 />,
                    false,
                  )}
                </CompanyCenterGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/users-h5"
            element={
              <ProtectedRoute allowedRoles={SETTINGS_USERS_ROLES}>
                <CompanyCenterGuard>
                  {renderDualModeElement(
                    'settingsUsers',
                    preferMobileLayout,
                    <UserAccountManage />,
                    <UserAccountManageH5 />,
                    true,
                  )}
                </CompanyCenterGuard>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings/workflows"
            element={
              <ProtectedRoute allowedRoles={SETTINGS_WORKFLOW_ROLES}>
                <CompanyCenterGuard>
                  {renderDualModeElement(
                    'settingsWorkflows',
                    preferMobileLayout,
                    <WorkflowConfig />,
                    <WorkflowConfigH5 />,
                    false,
                  )}
                </CompanyCenterGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/workflows-h5"
            element={
              <ProtectedRoute allowedRoles={SETTINGS_WORKFLOW_ROLES}>
                <CompanyCenterGuard>
                  {renderDualModeElement(
                    'settingsWorkflows',
                    preferMobileLayout,
                    <WorkflowConfig />,
                    <WorkflowConfigH5 />,
                    true,
                  )}
                </CompanyCenterGuard>
              </ProtectedRoute>
            }
          />
          
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          <Route 
            path="/" 
            element={
              isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <Portal />
            } 
          />
          
          <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <LegalConsentGate />
          <SiteBeianFooter />
        </div>
        </LegalDocumentViewerProvider>
      </Router>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
